import React, { useEffect, useRef, useState, useCallback } from 'react';
import './LiveStreamViewer.scss';

export interface WebRtcSignalEvent {
  from_user_id: string;
  to_user_id: string;
  signal: {
    sdp?: RTCSessionDescriptionInit;
    candidate?: RTCIceCandidateInit;
    type?: string;
    reason?: string;
  };
}

export type ViewerMode = 'modal' | 'pip';
export type PipSize = 'compact' | 'medium' | 'large';

interface LiveStreamViewerProps {
  targetUserId: string;
  targetUserName: string;
  myUserId: string;
  ws: WebSocket | null;
  onClose: () => void;
  incomingSignal?: WebRtcSignalEvent | null;
  mode?: ViewerMode;
}

export const LiveStreamViewer: React.FC<LiveStreamViewerProps> = ({
  targetUserId,
  targetUserName,
  myUserId,
  ws,
  onClose,
  incomingSignal,
  mode = 'modal',
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const [status, setStatus] = useState<'connecting' | 'live' | 'error' | 'unavailable'>('connecting');
  const [statusMessage, setStatusMessage] = useState<string>('Connecting to player stream...');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pipSize, setPipSize] = useState<PipSize>('medium');
  const [isMinimized, setIsMinimized] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const retryCountRef = useRef(0);
  const maxAutoRetries = 5;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionGraceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionGraceTimerRef.current) {
      clearTimeout(connectionGraceTimerRef.current);
      connectionGraceTimerRef.current = null;
    }
  }, []);

  const startStream = useCallback(() => {
    clearTimers();

    if (!ws || ws.readyState !== WebSocket.OPEN) {
      setStatus('error');
      setStatusMessage('WebSocket not connected');
      return;
    }

    // Clean up any existing WebRTC connection
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch {}
      pcRef.current = null;
    }

    setStatus('connecting');
    setStatusMessage(
      retryCountRef.current > 0
        ? `Reconnecting to ${targetUserName} (attempt ${retryCountRef.current}/${maxAutoRetries})...`
        : `Requesting stream from ${targetUserName}...`
    );

    const configuration: RTCConfiguration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:19302' },
      ],
    };

    const pc = new RTCPeerConnection(configuration);
    pcRef.current = pc;

    // Receive video only
    pc.addTransceiver('video', { direction: 'recvonly' });

    // Dummy data channel keeps DTLS/NAT alive even during static frames
    try {
      pc.createDataChannel('keepalive', { negotiated: true, id: 0 });
    } catch {}

    pc.ontrack = (event) => {
      if (videoRef.current && event.streams[0]) {
        videoRef.current.srcObject = event.streams[0];
        setStatus('live');
        setStatusMessage('Live');
        retryCountRef.current = 0;
        videoRef.current.play().catch(() => {});
      }
    };

    pc.onicecandidate = (event) => {
      if (event.candidate && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'WebRtcSignal',
          data: {
            from_user_id: myUserId,
            to_user_id: targetUserId,
            signal: { candidate: event.candidate },
          },
        }));
      }
    };

    pc.onconnectionstatechange = () => {
      if (!pcRef.current || pcRef.current !== pc) return;

      if (pc.connectionState === 'connected') {
        setStatus('live');
        setStatusMessage('Live');
        retryCountRef.current = 0;
        clearTimers();
      } else if (pc.connectionState === 'disconnected') {
        // Disconnected is often transient in WebRTC. Give it 3.5 seconds to self-heal.
        setStatusMessage('Connection interrupted, waiting...');
        if (!connectionGraceTimerRef.current) {
          connectionGraceTimerRef.current = setTimeout(() => {
            if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
              triggerAutoReconnect();
            }
          }, 3500);
        }
      } else if (pc.connectionState === 'failed') {
        triggerAutoReconnect();
      }
    };

    pc.createOffer()
      .then((offer) => pc.setLocalDescription(offer))
      .then(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'WebRtcSignal',
            data: {
              from_user_id: myUserId,
              to_user_id: targetUserId,
              signal: { sdp: pc.localDescription },
            },
          }));
        }
      })
      .catch((err) => {
        console.warn('Failed to create WebRTC offer:', err);
        setStatus('error');
        setStatusMessage('Failed to start stream');
      });
  }, [clearTimers, myUserId, targetUserId, targetUserName, ws]);

  const triggerAutoReconnect = useCallback(() => {
    clearTimers();
    if (retryCountRef.current < maxAutoRetries) {
      retryCountRef.current += 1;
      const delay = Math.min(1000 * Math.pow(1.4, retryCountRef.current), 5000);
      setStatus('connecting');
      setStatusMessage(`Connection lost. Reconnecting in ${Math.round(delay / 1000)}s...`);
      reconnectTimeoutRef.current = setTimeout(() => {
        startStream();
      }, delay);
    } else {
      setStatus('error');
      setStatusMessage('Stream disconnected. Click Retry to reconnect.');
    }
  }, [clearTimers, startStream]);

  const manualReconnect = () => {
    retryCountRef.current = 0;
    startStream();
  };

  useEffect(() => {
    retryCountRef.current = 0;
    startStream();

    // Timeout if initial connection takes too long
    const timeout = setTimeout(() => {
      if (status === 'connecting') {
        setStatus('unavailable');
        setStatusMessage(`${targetUserName}'s game screen is not responding.`);
      }
    }, 15000);

    return () => {
      clearTimeout(timeout);
      clearTimers();
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({
            type: 'WebRtcSignal',
            data: {
              from_user_id: myUserId,
              to_user_id: targetUserId,
              signal: { type: 'hangup' },
            },
          }));
        } catch {}
      }
      if (pcRef.current) {
        try {
          pcRef.current.close();
        } catch {}
        pcRef.current = null;
      }
    };
  }, [targetUserId]);

  // Handle incoming signals
  useEffect(() => {
    if (!incomingSignal) return;
    const { from_user_id, signal } = incomingSignal;
    if (from_user_id !== targetUserId) return;

    const pc = pcRef.current;
    if (!pc) return;

    if (signal.type === 'unavailable') {
      setStatus('unavailable');
      setStatusMessage(signal.reason || `${targetUserName} is not currently running an emulator session.`);
      return;
    }

    if (signal.sdp && signal.sdp.type === 'answer') {
      pc.setRemoteDescription(new RTCSessionDescription(signal.sdp)).catch((err) => {
        console.warn('Failed to set remote description answer:', err);
      });
    } else if (signal.candidate) {
      pc.addIceCandidate(new RTCIceCandidate(signal.candidate)).catch((err) => {
        console.warn('Failed to add ICE candidate:', err);
      });
    }
  }, [incomingSignal, targetUserId, targetUserName]);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const cyclePipSize = () => {
    if (pipSize === 'compact') setPipSize('medium');
    else if (pipSize === 'medium') setPipSize('large');
    else setPipSize('compact');
  };

  const modalContent = (
    <div
      className={`live-stream-modal size-${pipSize} ${isFullscreen ? 'fullscreen' : ''} ${isMinimized ? 'minimized' : ''}`}
      ref={containerRef}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="live-stream-header">
        <div className="header-left">
          <span className={`status-indicator ${status}`} />
          <span className="live-badge">SPECTATING</span>
          <span className="player-name">{targetUserName}</span>
        </div>
        <div className="header-controls">
          {mode === 'pip' && (
            <>
              <button
                className="ctrl-btn size-btn"
                onClick={cyclePipSize}
                title={`Change Size (Current: ${pipSize})`}
              >
                {pipSize === 'compact' ? '▫' : pipSize === 'medium' ? '◻' : '◫'}
              </button>
              <button
                className="ctrl-btn minimize-btn"
                onClick={() => setIsMinimized((prev) => !prev)}
                title={isMinimized ? 'Restore Stream' : 'Minimize to Header'}
              >
                {isMinimized ? '▲' : '▼'}
              </button>
            </>
          )}
          <button
            className="ctrl-btn"
            onClick={manualReconnect}
            title="Refresh / Reconnect stream"
          >
            🔄
          </button>
          <button
            className="ctrl-btn"
            onClick={toggleFullscreen}
            title="Toggle Fullscreen"
          >
            {isFullscreen ? '⤦' : '⛶'}
          </button>
          <button
            className="ctrl-btn close-btn"
            onClick={onClose}
            title="Close Spectator Screen"
          >
            ✕
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="live-stream-body">
          <video
            ref={videoRef}
            className={`live-stream-video ${status === 'live' ? 'visible' : 'hidden'}`}
            autoPlay
            playsInline
            muted
          />

          {status !== 'live' && (
            <div className="live-stream-placeholder">
              {status === 'connecting' && (
                <div className="loading-spinner-wrapper">
                  <div className="stream-spinner" />
                  <p>{statusMessage}</p>
                </div>
              )}
              {status === 'unavailable' && (
                <div className="status-notice">
                  <div className="notice-icon">📺</div>
                  <h3>Stream Unavailable</h3>
                  <p>{statusMessage}</p>
                  <button className="stream-retry-btn" onClick={manualReconnect}>Retry Connection</button>
                </div>
              )}
              {status === 'error' && (
                <div className="status-notice">
                  <div className="notice-icon">⚠️</div>
                  <h3>Connection Error</h3>
                  <p>{statusMessage}</p>
                  <button className="stream-retry-btn" onClick={manualReconnect}>Retry</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (mode === 'pip') {
    return (
      <aside className="live-stream-pip-container" aria-label="Spectator Player">
        {modalContent}
      </aside>
    );
  }

  return (
    <div className="live-stream-overlay" onClick={onClose}>
      {modalContent}
    </div>
  );
};

export default LiveStreamViewer;
